/**
 * Help Center Page
 * Route: /help
 * Features: Search header, FAQ categories, accordion-style questions, contact support CTA
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import {
  ArrowLeft,
  Search,
  User,
  ShoppingBag,
  CreditCard,
  ShieldAlert,
  ChevronDown,
  ChevronUp,
  MessageCircle,
} from 'lucide-react';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  icon: React.ReactNode;
  items: FAQItem[];
}

const HelpCenterPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);

  const categories: FAQCategory[] = [
    {
      title: 'Account & Profile',
      icon: <User className="h-5 w-5" />,
      items: [
        {
          id: 'acc-1',
          question: 'How do I create an account on MemAds?',
          answer: 'Click on the "Login/Sign up" button at the top right of the page. You can register using your email and password, or quickly sign up using your Google account.',
        },
        {
          id: 'acc-2',
          question: 'How can I change my password?',
          answer: 'Go to Settings > Profile and password section. Enter your current password followed by your new password, then save the changes.',
        },
        {
          id: 'acc-3',
          question: 'I forgot my password. How do I recover it?',
          answer: 'On the Login page, click on the "Forgot Password" link. Enter your registered email address, and we will send you a password reset link.',
        },
      ],
    },
    {
      title: 'Buying & Searching',
      icon: <ShoppingBag className="h-5 w-5" />,
      items: [
        {
          id: 'buy-1',
          question: 'How do I search for products in my city?',
          answer: 'You can use the search bar at the top of the homepage. Select your region from the location dropdown (e.g. All India, specific state or city) to narrow down local listings.',
        },
        {
          id: 'buy-2',
          question: 'How do I contact a seller?',
          answer: 'Open the listing detail page. Under the seller details card, click on the "Chat with seller" button to start an instant message, or click the phone number button to request their number.',
        },
        {
          id: 'buy-3',
          question: 'Is it safe to buy pre-owned electronics?',
          answer: 'Yes, but we advise meeting the seller in a public place, thoroughly inspecting the device, verifying its working condition, and paying only after you are satisfied.',
        },
      ],
    },
    {
      title: 'Selling & Posting Ads',
      icon: <CreditCard className="h-5 w-5" />,
      items: [
        {
          id: 'sel-1',
          question: 'How do I post a new ad listing?',
          answer: 'Click the "+ Post Ad" button in the navigation header. Fill in the category, item details, select a location, upload clear photos, and submit. Your listing will go active immediately.',
        },
        {
          id: 'sel-2',
          question: 'What are the rules for posting ads?',
          answer: 'We do not allow spam, duplicate postings, illegal products, weapons, or misleading descriptions. All postings must comply with our Terms of Service.',
        },
        {
          id: 'sel-3',
          question: 'How do I mark my item as sold?',
          answer: 'Go to "My Listings" from your account dropdown, find the active ad, and click the "Mark as Sold" or "Edit" button to change its status to Sold.',
        },
      ],
    },
    {
      title: 'Trust & Safety',
      icon: <ShieldAlert className="h-5 w-5" />,
      items: [
        {
          id: 'saf-1',
          question: 'How do I report a suspicious ad?',
          answer: 'On every listing page, there is a flag icon or "Report Ad" button. Click it to choose a reason (e.g., scam, inappropriate content, incorrect price) and submit a report to our moderators.',
        },
        {
          id: 'saf-2',
          question: 'What should I do if a seller demands advance payment?',
          answer: 'Never pay in advance. If a seller requests online bank transfer, deposit, or shipping payment before you inspect the item in person, cancel the trade and report them immediately.',
        },
      ],
    },
  ];

  const toggleFAQ = (id: string) => {
    setExpandedFAQ((prev) => (prev === id ? null : id));
  };

  // Filter FAQs based on search
  const filteredCategories = categories.map((cat) => {
    const matchedItems = cat.items.filter(
      (item) =>
        item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return { ...cat, items: matchedItems };
  }).filter((cat) => cat.items.length > 0);

  return (
    <div className="min-h-screen bg-background" id="help-center-page">
      <SEO
        title="Help Center"
        description="Find answers to frequently asked questions about buying, selling, safety, account settings, and payments on MemAds."
        canonical="/help"
      />
      {/* Hero Search Header */}
      <section className="relative bg-gradient-to-r from-accent to-slate-800 py-16 px-4 text-center overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.05),transparent_40%)]" />
        <div className="relative max-w-2xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white transition mb-6">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </Link>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-white">How can we help you?</h1>
          <p className="text-sm text-white/80 mt-2">Search our knowledge base or browse FAQs below</p>
          
          <div className="relative mt-6 max-w-lg mx-auto">
            <Search className="absolute left-4 top-3.5 h-5 w-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search help topics, keywords, questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-border bg-background text-foreground text-sm shadow-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
      </section>

      {/* FAQs Sections */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {filteredCategories.length > 0 ? (
          <div className="space-y-10">
            {filteredCategories.map((cat) => (
              <section key={cat.title} className="bg-card rounded-2xl border border-border p-6 shadow-sm">
                <h2 className="flex items-center gap-2.5 font-heading text-lg font-bold text-foreground border-b border-border pb-3 mb-4">
                  <span className="text-primary">{cat.icon}</span>
                  {cat.title}
                </h2>
                <div className="divide-y divide-border">
                  {cat.items.map((item) => {
                    const isExpanded = expandedFAQ === item.id;
                    return (
                      <div key={item.id} className="py-4 first:pt-0 last:pb-0">
                        <button
                          onClick={() => toggleFAQ(item.id)}
                          className="flex w-full items-center justify-between text-left font-semibold text-sm text-foreground hover:text-primary transition"
                        >
                          <span>{item.question}</span>
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </button>
                        {isExpanded && (
                          <p className="mt-2.5 text-xs text-muted-foreground leading-relaxed">
                            {item.answer}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-sm text-muted-foreground">No matches found for "{searchQuery}"</p>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-4 text-xs font-semibold text-primary hover:underline"
            >
              Clear Search Filter
            </button>
          </div>
        )}

        {/* Still need help CTA */}
        <section className="mt-12 bg-primary-soft border border-primary/20 rounded-2xl p-6 text-center">
          <h3 className="font-heading text-base font-bold text-foreground">Still need help?</h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
            If you couldn't find the answer in our FAQs, please send us a message and our support team will get back to you.
          </p>
          <div className="mt-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-xs font-semibold text-primary-foreground shadow-md transition hover:brightness-105"
            >
              <MessageCircle className="h-4 w-4" />
              Contact Support
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HelpCenterPage;
